import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useToast } from "@/shared/ui/Toast";
import { hasMinRole } from "./permissions";
import { useAuth, selectIsAuth, selectUser, type Role } from "./store";

/** Gate child routes behind a valid session. Preserves the original path
 * so login can redirect back after success. */
export function ProtectedRoute() {
  const isAuth = useAuth(selectIsAuth);
  const location = useLocation();

  if (!isAuth) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

/**
 * Route-level role gate. Wrap a set of routes in `<RequireRole min="org_admin" />`
 * so a direct URL or hotkey to a page the user can't reach bounces them to the
 * dashboard with a toast — the sidebar hides the same links, this is the
 * belt-and-braces for deep links. The server still enforces the real rule.
 */
export function RequireRole({ min }: { min: Role }) {
  const { t } = useTranslation();
  const user = useAuth(selectUser);
  const { toast } = useToast();
  const ok = hasMinRole(user?.role, min);
  const warned = useRef(false);

  useEffect(() => {
    if (!ok && !warned.current) {
      warned.current = true;
      toast({
        title: t("auth.forbidden.title"),
        description: t("auth.forbidden.description"),
        tone: "danger",
      });
    }
  }, [ok, toast, t]);

  if (!ok) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * Forced password change gate. While the signed-in user carries
 * `must_change_password` (fresh invite / admin reset), every app route is
 * redirected to `/change-password`; the server mirrors this with a 403
 * (`password_change_required`) on all other endpoints.
 */
export function PasswordGate() {
  const user = useAuth(selectUser);
  if (user?.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }
  return <Outlet />;
}
